import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Dropdown } from 'semantic-ui-react';
import { Button, Field, Groupper, Header, Icon, Input, Label, Message, Modal, Table, Toast, Toolbar } from 'react-frontier';
import { AvailableItemForShipment, InventoryWarehouse, Location, SetLoading, Shipment, ShipmentItemDetail } from '../SiteClasses';
import { SearchAvailableItemsModal, DeliverShipmentModal, GenerateShipmentModal, SearchUserModal, SendShipmentModal } from '../components';
import { useBreadcrumbs, useUser } from '../hooks';
import { bindFormChange, formatFullAddress, formatPlacesTemp } from '../SiteUtil';
import Autocomplete from '../components/Autocomplete';
import SearchRequestModal from '../components/SearchRequestModal';
import UserAccess from '../UserAccess';
import moment from 'moment';
import Validator from '../Validator';
import API from '../API';

enum Modals{
	CONFIRM_DELETE = 1,
	SEARCH_ITEMS = 2,
	DELIVER = 3,
	SHIP = 4,
	ITEM_INFO = 5,
	GENERATE = 6,
	SEARCH_USER = 7,
	EDIT_RETURN_DATE = 8,
	ADD_ITEMS = 9,
	SEARCH_REQUEST = 10,
}

enum DestinationType{
	WAREHOUSE = 1,
	LOCATION = 2,
}

enum LocationType{
	ADDRESS = 1,
	ESTABLISHMENT = 2,
}

interface ShipmentForm{
	warehouse_id?: number,
	destination?: Location,
	item_ids: number[],
	date_return?: number,
	description?: string,
}

var ShipmentView = ()=>{
	var { id } = useParams();
	var navigate = useNavigate();
	var isCreate = id === 'create';
	var { setTitle } = useBreadcrumbs();
	var { hasAccess } = useUser();

	var [loadError, setLoadError] = useState<string>(null);
	var [shownModal, setShownModal] = useState<Modals>(null);
	var [shipment, setShipment] = useState<Shipment>(null);
	var [prompts, setPrompts] = useState<string[]>([]);
	var [selectedItem, setSelectedItem] = useState<ShipmentItemDetail>(null);
	var [selectedAddItems, setSelectedAddItems] = useState<AvailableItemForShipment[]>([]);
	var [removingItem, setRemovingItem] = useState<boolean>(false);
	var [modalPrompts, setModalPrompts] = useState<string[]>([]);
	var [editReturnDate, setEditReturnDate] = useState<number>(null);

	var [form, setForm] = useState<ShipmentForm>({ item_ids: [] });
	var [destinationType, setDestinationType] = useState<DestinationType>(DestinationType.LOCATION);
	var [locationQuery, setLocationQuery] = useState('');
	var [selectedItems, setSelectedItems] = useState<AvailableItemForShipment[]>([]);
	var [selectedRequest, setSelectedRequest] = useState<{ request_id: number }>(null);
	var [warehouses, setWarehouses] = useState<InventoryWarehouse[]>(null);
	var [locationType, setLocationType] = useState<LocationType>(LocationType.ADDRESS);

	useEffect(()=>{
		if(isCreate){
			setTitle('Crear envío');
			API.getWarehouses().then(res=>{
				if(res.error) return;
				setWarehouses(res.data);
			});
			setSelectedRequest(null);
			return;
		}
		API.getShipment(parseInt(id)).then(res=>{
			if(res.error) return setLoadError(res.message);
			setShipment(res.data);
			setTitle(`Envío #${res.data.shipment_id}`);
		}).catch(()=>{
			setLoadError('Hubo un error inesperado cargando el envío.');
		});
	}, [id]);

	var closeModal = ()=>{
		setModalPrompts([]);
		setShownModal(null);
	};

	var closeAddItems = ()=>{
		setSelectedAddItems([]);
		closeModal();
	};

	var onPlaceSelected = (loc: any)=>{
		var formatted = formatPlacesTemp(loc);
		setLocationQuery(formatFullAddress(formatted));
		setForm(f => ({ ...f, destination: formatted }));
	};

	var onItemSelected = (item: AvailableItemForShipment)=>{
		var already = selectedItems.find(i => i.item_id === item.item_id);
		if(already){
			setSelectedItems(prev => prev.filter(i => i.item_id !== item.item_id));
			setForm(f => ({ ...f, item_ids: f.item_ids.filter(id => id !== item.item_id) }));
			return;
		}
		setSelectedItems(prev => [...prev, item]);
		setForm(f => ({ ...f, item_ids: [...f.item_ids, item.item_id] }));
	};

	var onRemoveItem = (item_id: number)=>{
		setSelectedItems(prev => prev.filter(i => i.item_id !== item_id));
		setForm(f => ({ ...f, item_ids: f.item_ids.filter(id => id !== item_id) }));
	};

	var onRequestSelected = (request: { request_id: number }, setLoading: SetLoading)=>{
		setSelectedRequest({ request_id: request.request_id });
		setShownModal(null);
		setLoading(false);
	};

	var onAddItemSelected = (item: AvailableItemForShipment)=>{
		var already = selectedAddItems.find(i => i.item_id === item.item_id);
		if(already){
			setSelectedAddItems(prev => prev.filter(i => i.item_id !== item.item_id));
			return;
		}
		setSelectedAddItems(prev => [...prev, item]);
	};

	var onCreate = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			...(destinationType === DestinationType.WAREHOUSE ? {
				warehouse_id: [{ rule: 'number', label: 'Almacén de destino' }],
			} : {
				destination: [{ rule: (val: Location)=> !!val && !!(val.city || val.lattitude), prompt: 'Selecciona una dirección de destino válida.' }],
				date_return: [{ rule: 'number', prompt: 'La fecha de retorno es obligatoria.' }],
			}),
			item_ids: [{ rule: (val: number[])=> val && val.length > 0, prompt: 'Agrega al menos un equipo al envío.' }],
		});
		setPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.createShipment({
			request_id: selectedRequest?.request_id,
			request_reference_only: !!selectedRequest,
			warehouse_id: destinationType === DestinationType.WAREHOUSE ? form.warehouse_id : null,
			destination: destinationType === DestinationType.LOCATION ? form.destination : null,
			item_ids: form.item_ids,
			date_return: destinationType === DestinationType.WAREHOUSE ? undefined : form.date_return,
			description: form.description,
		}).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Envío creado correctamente.');
			navigate(`/shipments/${res.data.shipment_id}`);
		}).catch(()=>{
			setPrompts(['Hubo un error inesperado creando el envío.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onDelete = (setLoading: SetLoading)=>{
		setLoading(true);
		API.deleteShipment(parseInt(id)).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Envío eliminado correctamente.');

			var s = {...shipment}
			s.deleted = true;
			setShipment(s);
		}).catch(()=>{
			Toast.error('Hubo un error inesperado eliminando el envío.');
		}).finally(()=>{
			setLoading(false);
			closeModal();
		});
	};

	var onFormChange = bindFormChange(form, setForm);

	var openItemInfo = (item: ShipmentItemDetail)=>{
		setSelectedItem(item);
		setShownModal(Modals.ITEM_INFO);
	};

	var removeItem = ()=>{
		if(!selectedItem || removingItem) return;
		setRemovingItem(true);
		API.removeShipmentItem(shipment.shipment_id, selectedItem.item_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Equipo eliminado del envío.');
			setShipment(s => ({ ...s, items: s.items.filter(i => i.item_id !== selectedItem.item_id) }));
			closeModal();
		}).catch(()=>{
			Toast.error('Hubo un error inesperado. (SHP-RMV-1)');
		}).finally(()=>{
			setRemovingItem(false);
		});
	};

	var onAssignedUserSelected = (user: any, setLoading: SetLoading)=>{
		setLoading(true);
		API.editShipmentUser(shipment.shipment_id, user.user_id).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success('Coordinador actualizado.');
			setShipment(s => ({
				...s,
				user_assigned: user.user_id,
				user_assigned_name: `${user.first_name} ${user.last_name}`,
			}));
			setShownModal(null);
		}).catch(()=>{
			Toast.error('Hubo un error inesperado actualizando el coordinador.');
		}).finally(()=>{
			setLoading(false);
		});
	};

	var openEditReturnDate = ()=>{
		setEditReturnDate(shipment.date_return || null);
		setModalPrompts([]);
		setShownModal(Modals.EDIT_RETURN_DATE);
	};

	var openAddItems = ()=>{
		setSelectedAddItems([]);
		setShownModal(Modals.ADD_ITEMS);
	};

	var onEditReturnDate = (setLoading: SetLoading)=>{
		if(!editReturnDate) return setModalPrompts(['La fecha de retorno es requerida.']);

		setLoading(true);
		API.editShipmentReturnDate(shipment.shipment_id, editReturnDate).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			Toast.success('Fecha de retorno actualizada.');
			setShipment(s => ({ ...s, date_return: editReturnDate }));
			closeModal();
		}).catch(()=>{
			setModalPrompts(['Hubo un error inesperado actualizando la fecha de retorno.']);
		}).finally(()=>{
			setLoading(false);
		});
	};

	var onAddItems = (setLoading: SetLoading)=>{
		if(selectedAddItems.length === 0) return Toast.error('Selecciona al menos un equipo para agregar al envío.');

		var itemsToAdd = selectedAddItems.map(item => ({
			item_id: item.item_id,
			type_icon: item.type_icon,
			type_name: item.type_name,
			item_name: item.item_name,
			serial_number: item.serial_number,
			warehouse_name: item.warehouse_name,
			location_city: item.location_city,
			date_created: moment().unix(),
		} as ShipmentItemDetail));

		setLoading(true);
		API.addShipmentItems(shipment.shipment_id, selectedAddItems.map(item => item.item_id)).then(res=>{
			if(res.error) return Toast.error(res.message);

			setShipment(s => ({
				...s,
				items: [...(s.items || []), ...itemsToAdd],
			}));
			closeAddItems();
			Toast.success(itemsToAdd.length === 1 ? 'Equipo agregado al envío.' : 'Equipos agregados al envío.');
		}).catch(()=>{
			Toast.error('Hubo un error inesperado agregando equipos al envío.');
		}).finally(()=>{
			setLoading(false);
		});
	};

	if(loadError) return <Header text='Error' subtext={loadError} />;
	if(!isCreate && !shipment) return <Header loading text='Cargando' />;

	if(isCreate) return <Groupper
		title='Crear envío'
		style={{ marginTop: 20 }}
		fitted
		width={650}
		actions={<Button text='Crear' color='black' onClick={onCreate} />}
	>
		<div style={{ padding: 10 }}>
			<Field label='Tipo de destino' style={{ marginBottom: 10 }}>
				<Dropdown
					selection
					fluid
					value={destinationType}
					onChange={(e, { value })=>{
						setDestinationType(value as DestinationType);
						if(value === DestinationType.LOCATION){
							setForm(f => ({ ...f, warehouse_id: undefined }));
						}else{
							setForm(f => ({ ...f, destination: undefined }));
							setLocationQuery('');
						}
					}}
					options={[
						{ key: DestinationType.LOCATION, value: DestinationType.LOCATION, text: 'Dirección' },
						{ key: DestinationType.WAREHOUSE, value: DestinationType.WAREHOUSE, text: 'Almacén' },
					]}
				/>
			</Field>
			{destinationType === DestinationType.LOCATION ? (
				<>
				<Field label='Tipo de dirección' style={{ marginBottom: 10 }}>
					<Dropdown
						selection
						fluid
						value={locationType}
						onChange={(e, { value })=>{
							setLocationType(value as LocationType);
							setForm(f => ({ ...f, destination: undefined }));
							setLocationQuery('');
						}}
						options={[
							{ key: LocationType.ADDRESS, value: LocationType.ADDRESS, text: 'Dirección exacta' },
							{ key: LocationType.ESTABLISHMENT, value: LocationType.ESTABLISHMENT, text: 'Recinto / Lugar público' },
						]}
					/>
				</Field>
				<Field label='Destino'>
					<Autocomplete
						key={locationType}
						value={locationQuery}
						onChange={setLocationQuery}
						onPlaceSelected={onPlaceSelected}
						types={locationType === LocationType.ESTABLISHMENT ? ['establishment'] : ['address']}
						placeholder={locationType === LocationType.ESTABLISHMENT ? 'Buscar recinto o lugar público...' : 'Buscar dirección de destino...'}
					/>
				</Field>
				</>
			) : (
				<Field label='Almacén de destino'>
					<Dropdown
						selection
						fluid
						placeholder='Selecciona un almacén...'
						value={form.warehouse_id}
						onChange={(e, { value })=> setForm(f => ({ ...f, warehouse_id: value as number }))}
						options={warehouses ? warehouses.map(w => ({ key: w.warehouse_id, value: w.warehouse_id, text: w.warehouse_name })) : []}
						style={{ marginBottom: 10 }}
					/>
				</Field>
			)}
			<Field label='Solicitud' style={{ marginBottom: 10 }}>
				<>
					<Input
						value={selectedRequest ? `Solicitud #${selectedRequest.request_id}` : ''}
						readonly
						placeholder='Selecciona una solicitud...'
						button={<Button text='Seleccionar' onClick={()=> setShownModal(Modals.SEARCH_REQUEST)} />}
					/>
					{selectedRequest ? (
						<div style={{ marginTop: 8 }}>
							<Button text='Quitar solicitud' basic size='tiny' onClick={()=> setSelectedRequest(null)} />
						</div>
					) : null}
				</>
			</Field>
			{destinationType !== DestinationType.WAREHOUSE && (
				<Input
					label='Fecha de retorno'
					value={form.date_return}
					calendar={{ date: form.date_return, mode: 'date', format: 'DD/MM/YY' }}
					onChange={onFormChange('date_return')}
					style={{ width: '100%', marginBottom: 10 }}
				/>
			)}
			<Input
				label='Comentarios'
				value={form.description}
				onChange={onFormChange('description')}
				textarea
			/>
		</div>
		<Table
			title='Equipos'
			striped
			fitted
			style={{ marginTop: 0 }}
			collapsingIndexes={[0, 3, 4, 5]}
			headers={['ID' ,'', 'Tipo', 'Nombre', 'Ubicación', 'Distancia', '']}
			button={<Button text='Agregar' iconName='circle-plus' color='black' size='tiny' onClick={()=> setShownModal(Modals.SEARCH_ITEMS)} />}
		>
			{selectedItems.length === 0 ? (
				<Table.Cell row className='empty' colSpan={7}>
					Agrega equipos al envío
				</Table.Cell>
			) : selectedItems.map(item => (
				<Table.Row
					key={item.item_id}
					collapsingIndexes={[0, 3, 4, 5]}
					data={[
						item.item_id,
						<Icon name={item.type_icon as any} />,
						item.type_name,
						item.item_name || <Icon name='minus' />,
						item.warehouse_name
							? <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon name='warehouse' /><span>{item.warehouse_name}</span></div>
							: item.location_city
								? <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Icon name='location-dot' /><span>{item.location_city}</span></div>
								: <Icon name='minus' />,
						item.distance != null ? `${item.distance.toFixed(2)} km` : <Icon name='minus' />,
						<Button icon basic iconName='trash' size='tiny' onClick={()=> onRemoveItem(item.item_id)} />,
					]}
				/>
			))}
		</Table>
		<Message type='error' list={prompts} style={{ margin: '0 10px 10px' }} />
		<SearchAvailableItemsModal
			open={shownModal === Modals.SEARCH_ITEMS}
			onClose={closeModal}
			onSelected={onItemSelected}
			destination_zipcode={destinationType === DestinationType.LOCATION ? (form.destination?.zipcode || null) : null}
			selectedItems={selectedItems}
		/>
		<SearchRequestModal
			open={shownModal === Modals.SEARCH_REQUEST}
			onClose={closeModal}
			onSelected={onRequestSelected}
		/>
	</Groupper>;

	var activeShipmentItems = shipment.items?.filter(i => !i.deleted) ?? [];

	return <div>
		<Groupper
			title='Envío'
			style={{ marginTop: 20 }}
			fitted
			width={800}
		>
			{((hasAccess(UserAccess.INVENTORY.DELETE_INVENTORY_SHIPMENTS) && !shipment.deleted) || (hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY_SHIPMENTS) && activeShipmentItems.length > 0 && activeShipmentItems.every(i => i.delivered)) || (hasAccess(UserAccess.INVENTORY.EDIT_INVENTORY_SHIPMENTS_USER_COORDINATOR) && !shipment.deleted) || (hasAccess(UserAccess.INVENTORY.EDIT_INVENTORY_SHIPMENTS_RETURN_DATE) && !shipment.deleted && !!shipment.date_return)) && (
				<Toolbar stretch>
					{hasAccess(UserAccess.INVENTORY.EDIT_INVENTORY_SHIPMENTS_RETURN_DATE) && !shipment.deleted && !!shipment.date_return && (
						<Toolbar.Item text='Fecha retorno' iconName='calendar' onClick={openEditReturnDate} />
					)}
					{hasAccess(UserAccess.INVENTORY.EDIT_INVENTORY_SHIPMENTS_USER_COORDINATOR) && !shipment.deleted && (
						<Toolbar.Item text='Coordinador' iconName='user' onClick={() => setShownModal(Modals.SEARCH_USER)} />
					)}
					{hasAccess(UserAccess.INVENTORY.DELETE_INVENTORY_SHIPMENTS) && !shipment.deleted && (
						<Toolbar.Item text='Cancelar' iconName='trash' onClick={() => setShownModal(Modals.CONFIRM_DELETE)} />
					)}
					{hasAccess(UserAccess.INVENTORY.CREATE_INVENTORY_SHIPMENTS) && activeShipmentItems.length > 0 && activeShipmentItems.every(i => i.delivered) && (
						<Toolbar.Item text='Generar envío' iconName='code-fork' onClick={() => setShownModal(Modals.GENERATE)} />
					)}
				</Toolbar>
			)}
			<Table
				details
				striped
				fitted
				data={[
					['ID', shipment.shipment_id],				['Envío padre', shipment.parent_shipment_id ? `#${shipment.parent_shipment_id}` : <Icon name='minus' />],					['Origen', shipment.request_id ? `Solicitud #${shipment.request_id}` : <Icon name='minus' />],
					['Destino', shipment.warehouse_name || (shipment.destination ? formatFullAddress(shipment.destination) : <Icon name='minus' />)],
					['Creador', shipment.user_creator_name],
					['Creación', moment.unix(shipment.date_created).format('DD/MMM/YY HH:mm')],
					['Fecha retorno', shipment.date_return ? moment.unix(shipment.date_return).format('DD/MMM/YY HH:mm') : <Icon name='minus' />],
					['Inicio taquilla', shipment.box_office_start_date ? moment.unix(shipment.box_office_start_date).format('DD/MMM/YY HH:mm') : <Icon name='minus' />],
					['Coordinador', shipment.user_assigned_name || <Icon name='minus' />],
						['Estatus', (()=>{
							var allDelivered = activeShipmentItems.length > 0 && activeShipmentItems.every(i => i.delivered);
							var allShipped = activeShipmentItems.length > 0 && activeShipmentItems.every(i => i.shipped || i.delivered);
							var isOk = activeShipmentItems.length === 0 || (!!shipment.warehouse_id && allDelivered);
							return <Label color={shipment.deleted ? 'red' : isOk ? 'black' : allDelivered ? 'green' : allShipped ? 'blue' : 'yellow'}>
								{shipment.deleted ? 'Cancelado' : isOk ? 'Finalizado' : allDelivered ? 'Entregado' : allShipped ? 'Enviado' : 'Pendiente'}
							</Label>;
					})()],
					['Comentarios', shipment.description || <Icon name='minus' />],
				]}
			/>
			{shipment.events && shipment.events.length > 0 && (
				<Table
					title='Eventos'
					striped
					headers={['ID', 'Evento', 'Fecha', '']}
					fitted
				>
					{shipment.events.map(e => (
						<Table.Row
							key={e.date_id}
							collapsingIndexes={[0, 2, 3]}
							data={[
								e.date_id,
								e.event_name, 
								e.date ? moment.unix(e.date).format('DD/MMM/YY') : <Icon name='minus' />,
								<Button icon basic iconName='eye' size='small' as={Link} to={`/events/${e.date_id}`}  target='_blank'/>,
							]}
						/>
					))}
				</Table>
			)}
			<Table
				title='Equipos'
				striped
				headers={['ID', '', 'Tipo', 'Nombre', 'Serie', 'Guía', 'Paquetería', 'Estatus', '']}
				collapsingIndexes={[0, 1, 4, 5, 6, 7, 8]}
				centeredIndexes={[7]}
				fitted
				button={
					(activeShipmentItems.some(i => !i.shipped && !i.delivered) || activeShipmentItems.some(i => i.shipped && !i.delivered) || activeShipmentItems.some(i => !i.delivered)) ? (
						<Dropdown icon={null} pointing direction='left' trigger={(
							<Button text='Opciones' color='black' iconRight='caret-down' iconRightStyle={{ marginRight: 0, marginLeft: 5 }} size='small'/>
						)}>
							<Dropdown.Menu>
								{activeShipmentItems.some(i => !i.delivered) && (
									<Dropdown.Item onClick={openAddItems}>
										<Icon name='circle-plus' style={{ marginRight: 3 }} /> Agregar
									</Dropdown.Item>
								)}
								{activeShipmentItems.some(i => !i.shipped && !i.delivered) && (
									<Dropdown.Item onClick={()=> setShownModal(Modals.SHIP)}>
										<Icon name='truck' style={{ marginRight: 3 }} /> Enviar
									</Dropdown.Item>
								)}
								{activeShipmentItems.some(i => i.shipped && !i.delivered) && (
									<Dropdown.Item onClick={()=> setShownModal(Modals.DELIVER)}>
										<Icon name='box-open' style={{ marginRight: 3 }} /> Entregar
									</Dropdown.Item>
								)}

							</Dropdown.Menu>
						</Dropdown>
					) : null
				}
			>
				{shipment.items && shipment.items.length > 0 ? (
					shipment.items.map(item => {
						var status = item.delivered
							? { label: 'Entregado', color: 'green' }
							: item.shipped
							? { label: 'Enviado', color: 'blue' }
							: { label: 'Pendiente', color: null };
						return (
				<Table.Row
					key={item.item_id}
					collapsingIndexes={[0, 1, 4, 5, 6, 7, 8]}
					centeredIndexes={[7]}
					data={[
						<Link style={{ padding: 0, textDecoration: 'underline' }} to={`/item/${item.item_id}`} target='_blank' rel='noopener noreferrer'>
							{item.item_id}
						</Link>,
						<Icon name={item.type_icon as any} />,
						item.type_name,
						item.item_name || <Icon name='minus' />,
						item.serial_number || <Icon name='minus' />,
						item.tracking_number || <Icon name='minus' />,
						item.carrier_name || <Icon name='minus' />,
					<Label color={status.color as any}>{status.label}</Label>,
						<Dropdown direction='left' icon={<Icon name='ellipsis' style={{ margin: 0 }} />} className='icon'>
							<Dropdown.Menu direction='left'>
								<Dropdown.Item onClick={()=> openItemInfo(item)}>
									<Icon name='circle-info' style={{ marginRight: 3 }} /> Info
								</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>,
					]}
				/>
						);
					})
				) : (
				<Table.Cell row className='empty' colSpan={9}>
						No se encontraron registros
					</Table.Cell>
				)}
			</Table>
		</Groupper>
		<Modal open={shownModal === Modals.ITEM_INFO} onClose={closeModal} size='small'>
			<Modal.Header>Equipo #{selectedItem?.item_id}</Modal.Header>
			<Modal.Content style={{ paddingBottom: 0 }}>
				{selectedItem && (
					<Table
						details
						striped
						fitted
						data={[
							['ID', selectedItem.item_id],
							['Tipo', selectedItem.type_name],
							['Nombre', selectedItem.item_name || <Icon name='minus' />],
							['Serie', selectedItem.serial_number || <Icon name='minus' />],
							['Origen', selectedItem.warehouse_name
								? <span><Icon name='warehouse' style={{ marginRight: 4 }} />{selectedItem.warehouse_name}</span>
								: selectedItem.location_city
								? <span><Icon name='location-dot' style={{ marginRight: 4 }} />{selectedItem.location_city}</span>
								: <Icon name='minus' />],
							['Estatus', <Label color={selectedItem.delivered ? 'green' : selectedItem.shipped ? 'blue' : null as any}>
								{selectedItem.delivered ? 'Entregado' : selectedItem.shipped ? 'Enviado' : 'Pendiente'}
							</Label>],
							...(selectedItem.shipped ? [
								['Guía', selectedItem.tracking_number || <Icon name='minus' />],
								['Paquetería', selectedItem.carrier_name || <Icon name='minus' />],
								['Fecha de envío', selectedItem.date_shipped ? moment.unix(selectedItem.date_shipped).format('DD/MMM/YY HH:mm') : <Icon name='minus' />],
							] as [string, any][] : []),
							...(selectedItem.delivered ? [
								['Receptor', selectedItem.user_recipient_name || selectedItem.recipient_name || <Icon name='minus' />],
								['Contacto receptor', selectedItem.recipient_contact || <Icon name='minus' />],
								['Fecha de entrega', selectedItem.date_delivered ? moment.unix(selectedItem.date_delivered).format('DD/MMM/YY HH:mm') : <Icon name='minus' />],
							] as [string, any][] : []),
							...(selectedItem.user_creator_name ? [
								['Creado por', selectedItem.user_creator_name],
							] as [string, any][] : []),
							['Fecha de alta', selectedItem.date_created ? moment.unix(selectedItem.date_created).format('DD/MMM/YY HH:mm') : <Icon name='minus' />],
						]}
					/>
				)}
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				{selectedItem && !selectedItem.shipped && hasAccess(UserAccess.INVENTORY.EDIT_INVENTORY_SHIPMENTS) && (
					<Button text='Eliminar del envío' color='red' loading={removingItem} onClick={removeItem} />
				)}
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.CONFIRM_DELETE} onClose={closeModal} size='tiny'>
			<Modal.Header>Cancelar envío</Modal.Header>
			<Modal.Content>
				<Header text='¿Cancelar este envío?' subtext='Esta acción no se puede deshacer.' />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text='Cancelar envío' color='red' onClick={onDelete} />
			</Modal.Actions>
		</Modal>
		<Modal open={shownModal === Modals.EDIT_RETURN_DATE} onClose={closeModal} size='tiny'>
			<Modal.Header>Editar fecha de retorno</Modal.Header>
			<Modal.Content>
				<Input
					label='Fecha de retorno'
					value={editReturnDate}
					calendar={{ date: editReturnDate, mode: 'date', format: 'DD/MM/YY' }}
					onChange={(value)=> setEditReturnDate(value as number)}
					style={{ width: '100%' }}
				/>
				<Message type='error' list={modalPrompts} />
			</Modal.Content>
			<Modal.Actions>
				<Button text='Cerrar' basic onClick={closeModal} />
				<Button text='Guardar' color='black' onClick={onEditReturnDate} />
			</Modal.Actions>
		</Modal>
		{shipment && (
			<SendShipmentModal
				open={shownModal === Modals.SHIP}
				onClose={closeModal}
				shipment_id={shipment.shipment_id}
				items={shipment.items || []}
				onSent={(item_ids, data)=>{
					setShipment(s => {
						var updatedItems = s.items.map(i => item_ids.includes(i.item_id) ? {
							...i, shipped: true, carrier_name: data.carrier_name, tracking_number: data.tracking_number
						} : i);
						var active = updatedItems.filter(i => !i.deleted);
						return { ...s, items: updatedItems, shipped: active.length > 0 && active.every(i => i.shipped || i.delivered) };
					});
				}}
			/>
		)}
		{shipment && (
			<DeliverShipmentModal
				open={shownModal === Modals.DELIVER}
				onClose={closeModal}
				shipment_id={shipment.shipment_id}
				items={shipment.items || []}
				onDelivered={(item_ids)=>{
					setShipment(s => {
						var updatedItems = s.items.map(i => item_ids.includes(i.item_id) ? { ...i, delivered: true } : i);
						var active = updatedItems.filter(i => !i.deleted);
						return { ...s, items: updatedItems, delivered: active.length > 0 && active.every(i => i.delivered), shipped: active.length > 0 && active.every(i => i.shipped || i.delivered) };
					});
				}}
			/>
		)}
		{shipment && (
			<SearchAvailableItemsModal
				open={shownModal === Modals.ADD_ITEMS}
				onClose={closeAddItems}
				onSave={onAddItems}
				onSelected={onAddItemSelected}
				destination_zipcode={shipment.warehouse_id ? null : (shipment.destination?.zipcode || null)}
				selectedItems={selectedAddItems}
			/>
		)}
		{shipment && (
			<GenerateShipmentModal
				open={shownModal === Modals.GENERATE}
				onClose={closeModal}
				shipment={shipment}
				onGenerated={(new_shipment_id)=>{
					navigate(`/shipments/${new_shipment_id}`);
				}}
			/>
		)}
		<SearchUserModal
			open={shownModal === Modals.SEARCH_USER}
			onClose={closeModal}
			onSelected={onAssignedUserSelected}
			title='Seleccionar coordinador'
		/>
	</div>;
};

export default ShipmentView;
